import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Organization } from "@app/core/models/organization.model";
import { AnalyticsService } from "@kells/apis/analytics";
import { DataAccessService } from "@kells/apis/data-access";
import { Patient, PatientService } from "@kells/clinic-one/apis";
import { keepDefined } from "@kells/utils/observable/observable-operators";
import { OnboardingModalComponent } from "apps/patient-reporting/src/app/home/components/onboarding-modal/onboarding-modal.component";
import { EssentialPatientInfoModalComponent } from "apps/patient-reporting/src/app/shared/components/essential-patient-info-modal/essential-patient-info-modal.component";
import { essentialPatientFields } from "apps/patient-reporting/src/app/shared/constants/essentialPatientFields.constants";
import {
  Portals,
  portalsOrganizations,
  QueryParameters,
} from "apps/patient-reporting/src/app/shared/enums/query.enums";
import { getQueryParamsFromLink } from "apps/patient-reporting/src/app/shared/functions/get-query.functions";
import { UserLoggedInEvent } from "apps/patient-reporting/src/app/shared/models/analytics/global-events";
import {
  InfoCollectionCompletedEvent,
  OnboardingClosedEvent,
  OpenPaymentSuccessModal,
} from "apps/patient-reporting/src/app/shared/models/analytics/home-events";
import { SharedService } from "apps/patient-reporting/src/app/shared/shared.service";
import { combineLatest } from "rxjs";
import { filter, first, map, take } from "rxjs/operators";
import { SubSink } from "subsink";
import * as Sentry from "@sentry/browser";
import { PaymentSuccessModalComponent } from "../payment-success-modal/payment-success-modal.component";
import { IntegratedAuthService, AuthType } from "../../../auth";
import { SubscriptionPlan } from "@kells/interfaces/patient";
import { UpgradePlanService } from "@kells/oral-analytics/report";
import { VirtualCheckupHandoffService } from "../../../virtual-checkup-handoff/virtual-checkup-handoff.service";

@Component({
  templateUrl: "./app-container.component.html",
  styleUrls: ["./app-container.component.scss"],
})
export class AppContainerComponent implements OnInit, OnDestroy {
  private static readonly PORTAL_SIGNUP_WINDOW_MS = 24 * 60 * 60 * 1000;
  isUserIdentified = false;
  private isPortalSignupContext = false;
  private essentialInfoPromptedForPatientId: string | null = null;

  isAetna = false;
  portal: Portals | undefined;

  private _subs = new SubSink();

  public allOrganizations$ = this.data.organizations$;

  private loggedInUserEmail$ = this.auth.user$.pipe(
    keepDefined(),
    map((user) => user.email as string)
  );

  constructor(
    private analyticsService: AnalyticsService,
    private auth: IntegratedAuthService,
    private patientService: PatientService,
    private data: DataAccessService,
    private dialog: MatDialog,
    private sharedService: SharedService,
    private upgradePlanService: UpgradePlanService,
    private handoff: VirtualCheckupHandoffService
  ) {}

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  ngOnInit(): void {
    Sentry.setUser(null);

    this._subs.sink = this.initializeUser();
    this._subs.sink = this.identifyUserByEmail();

    this._subs.sink = this.data.loading$.subscribe((loading) => {
      if (!loading) {
        this.checkPlan();
        this.checkPayment();
      }
    });
  }

  private checkPlan() {
    if (!this.isPortalSignupContext) {
      this.sharedService.resetPlanSettings();
      return;
    }

    const plan =
      getQueryParamsFromLink(window.location.href)?.[QueryParameters.Plan] ||
      this.sharedService.localStoragePlan;

    const isValidPlan = Object.values(SubscriptionPlan).includes(
      plan as SubscriptionPlan
    );

    if (isValidPlan) {
      this.upgradePlanService
        .openUpgradePlanModal({
          selectedPlan: plan as SubscriptionPlan,
        })
        .subscribe(() => this.sharedService.resetPlanSettings());
    } else {
      this.sharedService.resetPlanSettings();
    }
  }

  private checkPayment() {
    const payment = getQueryParamsFromLink(window.location.href)?.[
      QueryParameters.Payment
    ];
    const paymentPlan = getQueryParamsFromLink(window.location.href)?.[
      QueryParameters.PaymentPlan
    ];
    if (payment === "success") {
      this.analyticsService.record(OpenPaymentSuccessModal({}));
      this.dialog.open(PaymentSuccessModalComponent, {
        maxWidth: "700px",
        panelClass: "ai-photo-scan-modal",
        backdropClass: "ai-photo-scan-modal-backdrop",
        disableClose: true,
        data: { paymentPlan },
      });
    }
  }

  private initializeUser() {
    return combineLatest([
      this.allOrganizations$,
      this.data.selectedPatient$,
      this.loggedInUserEmail$,
    ])
      .pipe(
        filter(([_, patient, email]) =>
          this.patientMatchesLoggedInUser(patient, email)
        )
      )
      .subscribe(([organizations, patient]) => {
      if (!this.portal) {
        this.setPortal();
      }

      const idOfPortal = this.findCurrentPortalOrganization(organizations)?.id;
      this.isPortalSignupContext = this.shouldUsePortalSignupContext(
        patient,
        organizations,
        idOfPortal
      );
      if (!this.isPortalSignupContext && !this.portal) {
        this.sharedService.resetPortalAndPlanSettings();
      }

      Sentry.setUser({ email: patient.email, id: patient.id });

      if (this.handoff.isActive()) {
        this.identifyUser(patient.email);
        return;
      }

      const patientHasPortalInOrganizations = patient.organizations?.some(
        (id) => id === idOfPortal
      );

      if (
        this.isPortalSignupContext &&
        !patientHasPortalInOrganizations &&
        idOfPortal
      ) {
        const patientOrganizations = [idOfPortal];
        const trackOrganizations = organizations
          .filter((org) => patientOrganizations.includes(org.id))
          .map((org) => org.name);

        this.patientService
          .updatePatient({
            id: patient.id,
            organizations: patientOrganizations,
          })
          .pipe(take(1))
          .subscribe(() => {
            this.identifyUser(patient.email, trackOrganizations);
            this._subs.sink = this.setUserByEmail();
          });
        return;
      } else if (patient.organizations?.length) {
        const trackOrganizations = organizations
          .filter((org) => patient.organizations?.includes(org.id))
          .map((org) => org.name);

        this.identifyUser(patient.email, trackOrganizations);
      } else {
        this.identifyUser(patient.email);
      }

      this.checkEssentialFields(patient);
    });
  }

  private shouldUsePortalSignupContext(
    patient: Patient,
    organizations: Organization[],
    portalOrganizationId?: string
  ): boolean {
    if (!this.portal || !portalOrganizationId || !patient.organizations?.length) {
      return false;
    }

    if (patient.is_onboarded) {
      return false;
    }

    const kellsOrganization = organizations.find(
      (organization) => organization.name.toLowerCase() === Portals.Kells
    );
    const isDefaultKellsOnlyMember =
      !!kellsOrganization &&
      patient.organizations.length === 1 &&
      patient.organizations[0] === kellsOrganization.id;
    const hasNoProfileData =
      !patient.firstName && !patient.lastName && !patient.phone;
    const isRecentSignup = this.wasRecentlyCreated(patient.created_datetime);

    return isDefaultKellsOnlyMember && hasNoProfileData && isRecentSignup;
  }

  private wasRecentlyCreated(createdDateTime?: string): boolean {
    if (!createdDateTime) {
      return false;
    }

    const createdAtMs = new Date(createdDateTime).getTime();
    if (Number.isNaN(createdAtMs)) {
      return false;
    }

    return (
      Date.now() - createdAtMs <= AppContainerComponent.PORTAL_SIGNUP_WINDOW_MS
    );
  }

  findCurrentPortalOrganization(
    organizations: Organization[]
  ): Organization | undefined {
    const organizationName = portalsOrganizations.find(
      (element) => element.portal.toLowerCase() === this.portal?.toLowerCase()
    )?.organizationName;

    return organizations?.find(
      (organization) =>
        organization.name.toLowerCase() === organizationName?.toLowerCase()
    );
  }

  private identifyUser(email: string | undefined, organizations?: string[]) {
    if (this.isUserIdentified || !email) {
      return;
    }
    this.isUserIdentified = true;
    this.analyticsService.identifyUser(email, {
      organizations: organizations?.join(",").toLowerCase(),
    });
    this.analyticsService.record(UserLoggedInEvent({}));
  }

  private identifyUserByEmail() {
    const pid = this.handoff.getPatientId();
    if (this.handoff.isActive() && pid) {
      this.data.updateSelectedPatient({ id: pid });
      return;
    }
    if (this.handoff.isActive()) {
      const email = this.handoff.getEmail();
      if (email) {
        this.data.updateSelectedPatient({ email });
        return;
      }
    }
    return this.loggedInUserEmail$.pipe(first()).subscribe((email) => {
      this.data.updateSelectedPatient({ email });
    });
  }

  checkEssentialFields(patient: Patient) {
    if (this.handoff.isActive()) {
      return;
    }

    const hasAllEssentialData = essentialPatientFields.every(
      (field: string) => !!(patient as any)?.[field]
    );

    // Use the resolved auth type from the observable to avoid a race with Auth0
    // SDK initialization. authType$ replays the current BehaviorSubject value so
    // the first() fires synchronously if already determined; otherwise it waits for
    // the first resolved state (SSO or AUTH0 or NONE-final).
    this._subs.sink = this.auth.authType$.pipe(first()).subscribe((authType) => {
      const isSsoSession = authType === AuthType.SSO;
      const needsSsoConfirm =
        isSsoSession && !patient.is_onboarded && hasAllEssentialData;

      if (needsSsoConfirm) {
        if (this.essentialInfoPromptedForPatientId === patient.id) {
          return;
        }
        this.openEssentialPatientInfoModal(patient, true, () => {
          if (!patient.is_onboarded) {
            this.openOnboardingModal(patient.id);
          }
        });
        return;
      }

      if (hasAllEssentialData) {
        if (patient.is_onboarded) {
          return;
        }
        this.openOnboardingModal(patient.id);
        return;
      }

      this.openEssentialPatientInfoModal(patient, false);
    });
  }

  private openEssentialPatientInfoModal(
    patient: Patient,
    confirmMode: boolean,
    afterConfirmed?: () => void
  ): void {
    if (this.essentialInfoPromptedForPatientId === patient.id) {
      return;
    }
    this.essentialInfoPromptedForPatientId = patient.id;

    this.dialog
      .open(EssentialPatientInfoModalComponent, {
        disableClose: true,
        panelClass: "ai-photo-scan-modal",
        backdropClass: "ai-photo-scan-modal-backdrop",
        maxWidth: "1000px",
        width: "100%",
        data: confirmMode ? { patient, confirmMode: true } : patient,
      })
      .afterClosed()
      .subscribe(() => {
        this.analyticsService.record(InfoCollectionCompletedEvent({}));
        this._subs.sink = this.setUserByEmail();
        afterConfirmed?.();
      });
  }

  private patientMatchesLoggedInUser(
    patient: Patient,
    email: string | undefined
  ): boolean {
    if (!patient?.email || !email) {
      return false;
    }

    return patient.email.toLowerCase() === email.toLowerCase();
  }

  private openOnboardingModal(patientId: string, is_onboarded = false): void {
    this.dialog
      .open(OnboardingModalComponent, {
        width: "80%",
        maxWidth: "1280px",
        maxHeight: "100%",
        panelClass: "onboarding-modal",
        backdropClass: "onboarding-modal-backdrop",
        data: this.isAetna,
      })
      .afterClosed()
      .subscribe(() => {
        this.analyticsService.record(OnboardingClosedEvent({}));
        if (is_onboarded) {
          return;
        }
        this.patientService
          .updatePatient({
            is_onboarded: true,
            id: patientId,
          })
          .pipe(take(1))
          .subscribe();
      });
  }

  private setPortal() {
    const queryPortal = getQueryParamsFromLink(window.location.href)?.[
      QueryParameters.Portal
    ] as Portals;
    const localStoragePortal = this.sharedService.localStoragePortal;
    this.portal = queryPortal || localStoragePortal;
    this.isAetna = this.portal === Portals.Aetna;
  }

  private setUserByEmail() {
    return this.loggedInUserEmail$.pipe(take(1)).subscribe((email) => {
      this.data.updateSelectedPatient({ email });
    });
  }
}
